import { useEffect, useRef, useState } from 'react'
import { ArrowLeft, ArrowRight, List, Plus, X } from 'lucide-react'
import type { Translations } from '@/i18n/translations'
import { PracticeBook } from './PracticeBook'
import './join-explorer.css'

type BecomePro = Translations['becomePro']

/** One copy of every paragraph: desktop accordion and mobile product gallery. */
export function JoinExplorer({ text }: { text: BecomePro }) {
  const [open, setOpen] = useState(0)
  const [readAll, setReadAll] = useState(false)
  const [inView, setInView] = useState(0)
  const [compact, setCompact] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const items = useRef<(HTMLLIElement | null)[]>([])
  const triggers = useRef<(HTMLButtonElement | null)[]>([])
  const chips = useRef<(HTMLButtonElement | null)[]>([])
  const chipRail = useRef<HTMLDivElement>(null)
  const closeButton = useRef<HTMLButtonElement>(null)
  const canvas = useRef<HTMLDivElement>(null)
  const swipeStart = useRef<{ x: number; y: number } | null>(null)
  const active = readAll && !compact ? inView : open
  const expanded = readAll && !compact

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 899px)')
    const sync = () => setCompact(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  useEffect(() => {
    if (compact && detailsOpen) {
      closeButton.current?.focus({ preventScroll: true })
      canvas.current?.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'instant' })
    }
  }, [compact, detailsOpen])

  useEffect(() => {
    if (!compact || detailsOpen) return
    const chip = chips.current[open]
    if (chip && chipRail.current) chipRail.current.scrollLeft = chip.offsetLeft - 20
  }, [compact, detailsOpen, open])

  // Only desktop's long reading mode follows document scrolling.
  useEffect(() => {
    if (!readAll || compact) return
    let frame = 0
    const update = () => {
      frame = 0
      const edge = window.innerHeight * 0.45
      let current = 0
      items.current.forEach((item, i) => {
        if (item && item.getBoundingClientRect().top <= edge) current = i
      })
      setInView(current)
    }
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update) }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [readAll, compact])

  const step = (delta: number) => setOpen((value) => (value + delta + text.reasons.length) % text.reasons.length)
  const closeDetails = () => {
    setDetailsOpen(false)
    requestAnimationFrame(() => chips.current[open]?.focus({ preventScroll: true }))
  }

  return (
    <div className={`pro-join-explorer${readAll ? ' is-reading' : ''}${detailsOpen ? ' has-details' : ''}`} role="region" aria-label={text.a11y.region} onKeyDown={(event) => {
      if (compact && detailsOpen && event.key === 'Escape') { event.preventDefault(); closeDetails() }
    }}>
      <div className="pro-join-toolbar">
        <span className="pro-join-index">01 — 08 <span>{text.explore}</span></span>
        <button
          type="button"
          className="pro-join-reading"
          aria-pressed={readAll}
          aria-controls="practice-reasons"
          onClick={() => setReadAll((value) => !value)}
        >
          <List size={16} aria-hidden="true" />
          {readAll ? text.readOne : text.readAll}
        </button>
      </div>
      <div className="pro-join-canvas" ref={canvas}>
        <div className="pro-join-stage" aria-hidden="true" onTouchStart={(event) => {
          swipeStart.current = null
          if (!compact || event.touches.length !== 1) return
          swipeStart.current = { x: event.touches[0].clientX, y: event.touches[0].clientY }
        }} onTouchEnd={(event) => {
          const start = swipeStart.current
          swipeStart.current = null
          if (!start || !event.changedTouches.length) return
          const dx = event.changedTouches[0].clientX - start.x
          const dy = event.changedTouches[0].clientY - start.y
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) step(dx < 0 ? 1 : -1)
        }} onTouchCancel={() => { swipeStart.current = null }}>
          <div className="pro-join-stage-label"><span>HUB4PSY / {text.bookLabel}</span><span>{text.reasons[active].n} / 08</span></div>
          <PracticeBook state={active} framed={compact} />
          <span className="pro-join-caption">{text.reasons[active].alt}</span>
        </div>
        <button ref={closeButton} type="button" className="pro-join-close" hidden={!detailsOpen} aria-label={text.a11y.closeDetails} onClick={closeDetails}><X size={22} aria-hidden="true" /></button>
        <ol className="pro-join-list" id="practice-reasons" role="list">
          {text.reasons.map((reason, i) => {
            const isOpen = compact ? detailsOpen && i === open : expanded || i === open
            const label = <><span className="pro-join-n" aria-hidden="true">{reason.n}</span><span className="pro-join-label">{reason.title}</span></>
            return (
              <li key={reason.n} ref={(node) => { items.current[i] = node }} className={`pro-join-item${isOpen ? ' is-open' : ''}${i === active ? ' is-current' : ''}`}>
                <span className="pro-join-pill-bg" aria-hidden="true" />
                <div className="pro-join-mask">
                  <h3 className="pro-join-head-row" id={`join-${reason.n}`}>
                    {expanded || compact ? <span className="pro-join-trigger">{label}</span> : (
                      <button
                        type="button"
                        ref={(node) => { triggers.current[i] = node }}
                        className="pro-join-trigger"
                        aria-expanded={isOpen}
                        aria-controls={`join-panel-${reason.n}`}
                        title={isOpen ? text.a11y.close : undefined}
                        onClick={() => i === open ? step(1) : setOpen(i)}
                        onKeyDown={(event) => {
                          const target = event.key === 'ArrowDown' ? (i + 1) % 8
                            : event.key === 'ArrowUp' ? (i + 7) % 8
                              : event.key === 'Home' ? 0 : event.key === 'End' ? 7 : undefined
                          if (target !== undefined) { event.preventDefault(); triggers.current[target]?.focus() }
                        }}
                      >
                        {label}
                        <span className="pro-join-plus" aria-hidden="true">{isOpen ? <X size={15} /> : <Plus size={15} />}</span>
                      </button>
                    )}
                  </h3>
                  <div id={`join-panel-${reason.n}`} className="pro-join-region" role="group" aria-labelledby={`join-${reason.n}`} inert={!isOpen}>
                    <div className="pro-join-region-inner">
                      <p className="pro-join-body">{reason.body}</p>
                      {reason.sources && <ul className="pro-join-sources">{reason.sources.map((source) => <li key={source}>{source}</li>)}</ul>}
                      {reason.figure && <p className="pro-join-figure"><strong>{reason.figure.value}</strong><span>{reason.figure.caption}</span></p>}
                      <span className="sr-only">{reason.alt}</span>
                    </div>
                  </div>
                </div>
              </li>
            )
          })}
        </ol>
        <div className="pro-join-chips" ref={chipRail} aria-label={text.a11y.topics} hidden={detailsOpen}>
          {text.reasons.map((reason, i) => <button type="button" key={reason.n} ref={(node) => { chips.current[i] = node }} className="pro-join-chip" aria-current={i === open ? 'true' : undefined} aria-expanded={false} aria-controls={`join-panel-${reason.n}`} onClick={() => { setOpen(i); setDetailsOpen(true) }}><Plus size={21} aria-hidden="true" /><span>{text.chips[i]}</span></button>)}
        </div>
        {/* These buttons stay mounted in one place, so stepping preserves focus. */}
        <div className="pro-join-nav" hidden={expanded}>
          <button type="button" className="pro-join-paddle" aria-label={text.a11y.prev} onClick={() => step(-1)}><ArrowLeft size={18} aria-hidden="true" /></button>
          <span className="pro-join-position" aria-hidden="true">{text.reasons[open].n} / 08</span>
          <button type="button" className="pro-join-paddle" aria-label={text.a11y.next} onClick={() => step(1)}><ArrowRight size={18} aria-hidden="true" /></button>
        </div>
      </div>
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">{!expanded ? `${text.reasons[open].n}. ${text.reasons[open].title}` : ''}</p>
    </div>
  )
}
